import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GeoCodeResult, GeoSource } from '@/hotdesk-ui/types/geo-code-result.type';
import { StorageKeys } from '@/hotdesk-ui/enum/storage-keys';
import { storeGet, storeSet } from '@/hotdesk-ui/helpers/utils';
import { serializeGooglePlaceData } from '@/hotdesk-ui/helpers/location.helper';
import { geoDebug } from '@/hotdesk-ui/helpers/debug-geo';
import storage from 'redux-persist/lib/storage'; // Import directly from redux-persist
import { persistReducer } from 'redux-persist';

const geoCodeApi = 'https://maps.googleapis.com/maps/api/geocode/json';

interface GeoLocationState {
    geoLocationZipData: GeoCodeResult | null;
    sourceOfLocationData: GeoSource | null;
    /** Fresh ISO-2 country from edge IP headers on the current request (not persisted). */
    sessionIpCountry: string | null;
    error: string | null;
    loading: boolean;
    showLocationModal: boolean
}

const initialState: GeoLocationState = {
    geoLocationZipData: storeGet<GeoCodeResult | null>(StorageKeys.GEO_LOCATION_DATA) || null,
    sourceOfLocationData:
        storeGet<GeoCodeResult | null>(StorageKeys.GEO_LOCATION_DATA)?.source || null,
    sessionIpCountry: null,
    error: null,
    loading: false,
    showLocationModal: false
};

export const fetchLocationAPI = createAsyncThunk(
    'geoLocation/fetchLocationAPI', 
    async (payload: {latitude: number, longitude: number}) => {
        try {
            const { latitude, longitude } = payload;
            const response = await fetch(`${geoCodeApi}?key=${process.env.NEXT_PUBLIC_FIREBASE_API_KEY}&latlng=${latitude},${longitude}`);
            if (!response.ok) throw new Error('Failed to fetch geocode data');
            const data = await response.json();
            const { results = [] } = data;
            const result = serializeGooglePlaceData(results[0]);
            result.lat = latitude;
            result.log = longitude;
            result.source = 'user-geolocation';
            storeSet(StorageKeys.GEO_LOCATION_DATA, result);
            storeSet('sourceOfLocationData', 'user-geolocation');
            return result;
        } catch (error) {
            throw new Error('Failed to fetch geolocation data.');
        }
    })

export const fetchGeoLocation = createAsyncThunk<
    GeoCodeResult,
    void,
    { rejectValue: string }
>('geoLocation/fetchGeoLocation', async (_, { dispatch, rejectWithValue }) => {
    if (!navigator.geolocation) {
        return rejectWithValue('Geolocation is not supported by this browser.');
    }

    return new Promise<GeoCodeResult>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                await dispatch(fetchLocationAPI({latitude, longitude}))
            },
            (error) => {
                reject(error.message || 'Failed to get geolocation.');
            }
        );
    });
});

export const fetchAll = createAsyncThunk(
    'geoLocation/fetchAll',
    async (_, { dispatch }) => {
        try {
            const geoLocationData = storeGet<GeoCodeResult>(StorageKeys.GEO_LOCATION_DATA);
            if(!geoLocationData) {
                await dispatch(fetchGeoLocation());
            } else {
                dispatch(setGeoLocation(geoLocationData))
            }
        } catch (error) {
            console.error('Error during initialization:', error);
        }
    }
);

const geoLocationSlice = createSlice({
    name: 'geoLocation',
    initialState,
    reducers: {
        setGeoLocation(state, action) {
            state.geoLocationZipData  = action.payload;
            if (action.payload?.source) {
                state.sourceOfLocationData = action.payload.source;
                storeSet('sourceOfLocationData', action.payload.source);
            }
            storeSet(StorageKeys.GEO_LOCATION_DATA, state.geoLocationZipData);
            geoDebug('redux setGeoLocation', {
                country_sort: action.payload?.country_sort,
                country: action.payload?.country,
                city: action.payload?.city,
                formatted_address: action.payload?.formatted_address
            });
        },
        resetGeoLocation(state) {
            state.geoLocationZipData = null;
            state.sourceOfLocationData = null;
            storeSet(StorageKeys.GEO_LOCATION_DATA, null);
            storeSet('sourceOfLocationData', null);
        },
        setSourceOfLocationData(state, action: PayloadAction<GeoSource>) {
            state.sourceOfLocationData = action.payload;
            storeSet('sourceOfLocationData', action.payload);
        },
        toggelLocationPopup(state, action) {
            state.showLocationModal = action.payload
        },
        setSessionIpCountry(state, action: PayloadAction<string | null>) {
            state.sessionIpCountry = action.payload;
            geoDebug('redux setSessionIpCountry', { country: action.payload });
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLocationAPI.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLocationAPI.fulfilled, (state, action: PayloadAction<GeoCodeResult>) => {
                state.loading = false;
                state.geoLocationZipData = action.payload;
                state.sourceOfLocationData = 'user-geolocation';
            })
            .addCase(fetchLocationAPI.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    resetGeoLocation,
    toggelLocationPopup,
    setGeoLocation,
    setSessionIpCountry,
    setSourceOfLocationData
} = geoLocationSlice.actions;
const persistConfig = {
    key: 'geolocation',
    storage: storage,
    whitelist: ['geoLocationZipData', 'sourceOfLocationData']
}

export default persistReducer(persistConfig, geoLocationSlice.reducer);
